import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Modal, Form, Table, ProgressBar, Spinner } from 'react-bootstrap';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import './CircularTrack.css';
import circularTrackService, { 
  TrackVehicle, 
  ThermalAlert, 
  SystemStatus, 
  ThermalData,
  PatrolStatistics,
  AnomalyStatistics,
  FireSafetyStatus,
  FireEvent
} from '../../services/circularTrackService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface PatrolData {
  timestamp: string;
  temperature: number;
  vehicleCount: number;
  alertCount: number;
}

const CircularTrack: React.FC = () => {
  // 狀態管理
  const [trackVehicles, setTrackVehicles] = useState<TrackVehicle[]>([]);
  const [thermalAlerts, setThermalAlerts] = useState<ThermalAlert[]>([]);
  const [patrolData, setPatrolData] = useState<PatrolData[]>([]);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [thermalData, setThermalData] = useState<ThermalData[]>([]);
  const [patrolStats, setPatrolStats] = useState<PatrolStatistics | null>(null);
  const [anomalyStats, setAnomalyStats] = useState<AnomalyStatistics | null>(null);
  const [fireSafetyStatus, setFireSafetyStatus] = useState<FireSafetyStatus | null>(null);
  const [fireEvents, setFireEvents] = useState<FireEvent[]>([]);
  const [selectedFireDevice, setSelectedFireDevice] = useState<any>(null);
  const [fireDeviceModalOpen, setFireDeviceModalOpen] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showControlModal, setShowControlModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<TrackVehicle | null>(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<ThermalAlert | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);

  // 錯誤處理和重試機制
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRY_COUNT = 3;

  const handleError = useCallback((error: any, operation: string) => {
    console.error(`${operation}失敗:`, error);
    
    if (retryCount < MAX_RETRY_COUNT) {
      setRetryCount(prev => prev + 1);
      setTimeout(() => {
        console.log(`重試${operation} (${retryCount + 1}/${MAX_RETRY_COUNT})`);
        // Will call refreshData when it's available
      }, 2000 * (retryCount + 1)); // 指數退避
    } else {
      setError(`${operation}失敗，請檢查網絡連接或聯繫管理員`);
    }
  }, [retryCount]);

  // 網絡狀態監控
  useEffect(() => {
    const handleOnline = () => {
      console.log('網絡連接恢復');
      setRetryCount(0);
      // Will refresh data when network is back
    };

    const handleOffline = () => {
      console.log('網絡連接斷開');
      setError('網絡連接斷開，請檢查網絡設置');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 初始化數據加載
  useEffect(() => {
    loadInitialData();
    setupWebSocket();
    
    // 設置定時刷新
    const interval = setInterval(() => {
      refreshData();
    }, 30000); // 每30秒刷新一次

    return () => {
      clearInterval(interval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        vehiclesData,
        alertsData,
        statusData,
        thermalDataResult,
        patrolStatsData,
        anomalyStatsData,
        fireStatusData
      ] = await Promise.all([
        circularTrackService.getTrackVehicles(),
        circularTrackService.getThermalAlerts({ limit: 10 }),
        circularTrackService.getSystemStatus(),
        circularTrackService.getThermalData(),
        circularTrackService.getPatrolStatistics(),
        circularTrackService.getAnomalyStatistics(),
        circularTrackService.getFireSafetyStatus()
      ]);

      setTrackVehicles(vehiclesData);
      setThermalAlerts(alertsData.data);
      setSystemStatus(statusData);
      setThermalData(thermalDataResult);
      setPatrolStats(patrolStatsData);
      setAnomalyStats(anomalyStatsData);
      setFireSafetyStatus(fireStatusData);

      // 獲取消防事件
      const events = await circularTrackService.getFireEvents(5);
      setFireEvents(events);
    } catch (err) {
      console.error('加載數據失敗:', err);
      setError('加載數據失敗，請稍後重試');
    } finally {
      setLoading(false);
    }
  };

  // 數據緩存和性能優化
  const [dataCache, setDataCache] = useState<{[key: string]: {data: any, timestamp: number}}>({});
  const CACHE_DURATION = 30000; // 30秒緩存

  const getCachedData = useCallback(async (key: string, fetchFunction: () => Promise<any>) => {
    const now = Date.now();
    const cached = dataCache[key];
    
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      return cached.data;
    }
    
    try {
      const data = await fetchFunction();
      setDataCache(prev => ({
        ...prev,
        [key]: { data, timestamp: now }
      }));
      return data;
    } catch (error) {
      console.error(`獲取數據失敗 (${key}):`, error);
      // 如果有緩存數據，返回緩存數據
      if (cached) {
        return cached.data;
      }
      throw error;
    }
  }, [dataCache]);

  // 優化的數據刷新函數
  const refreshData = useCallback(async () => {
    if (loading) return; // 防止重複請求
    
    try {
      // 並行獲取關鍵數據
      const [vehiclesData, alertsData, statusData] = await Promise.all([
        getCachedData('vehicles', () => circularTrackService.getTrackVehicles()),
        getCachedData('alerts', () => circularTrackService.getThermalAlerts({ limit: 10 })),
        getCachedData('status', () => circularTrackService.getSystemStatus())
      ]);

      setTrackVehicles(vehiclesData);
      setThermalAlerts(alertsData.data);
      setSystemStatus(statusData);
    } catch (err) {
      console.error('刷新數據失敗:', err);
    }
  }, [loading, getCachedData]);

  const setupWebSocket = () => {
    const ws = circularTrackService.connectWebSocket({
      onVehicleUpdate: (data) => {
        setTrackVehicles(prev => {
          const updated = [...prev];
          const index = updated.findIndex(vehicle => vehicle.id === data.id);
          if (index >= 0) {
            updated[index] = data;
          } else {
            updated.push(data);
          }
          return updated;
        });
      },
      onThermalAlert: (data) => {
        setThermalAlerts(prev => {
          const updated = [...prev];
          const index = updated.findIndex(alert => alert.id === data.id);
          if (index >= 0) {
            updated[index] = data;
          } else {
            updated.unshift(data);
          }
          return updated.slice(0, 50); // 保持最新50條
        });
      },
      onFireAlert: (data) => {
        console.log('收到火災警報:', data);
        // Handle fire alert updates
      },
      onEmergencyStop: (data) => {
        console.log('緊急停止:', data);
        setSystemStatus(prev => prev ? { ...prev, emergencyMode: true } : null);
      }
    });

    if (ws) {
      wsRef.current = ws;
      
      ws.onerror = (error) => {
        console.error('WebSocket連接錯誤:', error);
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket連接關閉:', event.code, event.reason);
        
        // 如果不是正常關閉，嘗試重連
        if (event.code !== 1000) {
          setTimeout(() => {
            console.log('嘗試重連WebSocket');
            setupWebSocket();
          }, 5000);
        }
      };
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      active: { variant: 'success', text: '巡邏中' },
      idle: { variant: 'secondary', text: '待機' },
      charging: { variant: 'info', text: '充電中' },
      maintenance: { variant: 'warning', text: '維護中' },
      emergency: { variant: 'danger', text: '緊急狀態' }
    };
    const config = statusMap[status as keyof typeof statusMap] || statusMap.idle;
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getAlertBadge = (level: string) => {
    const levelMap = {
      warning: { variant: 'warning', text: '警告' },
      critical: { variant: 'danger', text: '嚴重' },
      emergency: { variant: 'dark', text: '緊急' }
    };
    const config = levelMap[level as keyof typeof levelMap] || levelMap.warning;
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  // 控制操作
  const handleVehicleControl = async (vehicleId: string, action: string) => {
    try {
      await circularTrackService.controlTrackVehicle(vehicleId, action as any);
      await refreshData();
      setShowControlModal(false);
    } catch (err) {
      console.error('控制軌道車失敗:', err);
      setError('控制軌道車失敗');
    }
  };

  const handleAutoPatrolToggle = async () => {
    try {
      if (systemStatus) {
        const action = systemStatus.autoPatrol ? 'stop' : 'start';
        await circularTrackService.controlAutoPatrol(action);
        await refreshData();
      }
    } catch (err) {
      console.error('切換自動巡邏失敗:', err);
      setError('切換自動巡邏失敗');
    }
  };

  const handleEmergencyStop = async () => {
    try {
      await circularTrackService.emergencyStop();
      await refreshData();
    } catch (err) {
      console.error('緊急停止失敗:', err);
      setError('緊急停止失敗');
    }
  };

  const handleFireAlert = async (alertId: string) => {
    try {
      await circularTrackService.triggerFireAlert(alertId);
      await refreshData();
    } catch (err) {
      console.error('觸發消防警報失敗:', err);
      setError('觸發消防警報失敗');
    }
  };

  const handleAlertStatusUpdate = async (alertId: string, status: string) => {
    try {
      await circularTrackService.updateThermalAlert(alertId, { status: status as any });
      await refreshData();
      setShowAlertModal(false);
    } catch (err) {
      console.error('更新警報狀態失敗:', err);
      setError('更新警報狀態失敗');
    }
  };

  // 消防系統控制
  const handleFireSystemControl = async (action: string, options: any = {}) => {
    try {
      await circularTrackService.controlFireSystem(action, options);
      
      // 重新獲取消防狀態
      const fireStatus = await circularTrackService.getFireSafetyStatus();
      setFireSafetyStatus(fireStatus);
      
      // 如果是解決警報，重新獲取事件
      if (action === 'resolve_alert') {
        const events = await circularTrackService.getFireEvents(5);
        setFireEvents(events);
      }
      
      console.log('消防系統操作成功');
    } catch (error) {
      console.error('消防系統操作失敗:', error);
      setError('消防系統操作失敗');
    }
  };

  // 觸發手動火災警報
  const handleManualFireAlarm = async (zone: number, reason: string) => {
    try {
      await circularTrackService.triggerManualFireAlarm(zone, reason);
      console.log('手動火災警報已觸發');
      
      // 重新獲取狀態
      const fireStatus = await circularTrackService.getFireSafetyStatus();
      setFireSafetyStatus(fireStatus);
    } catch (error) {
      console.error('觸發火災警報失敗:', error);
      setError('觸發火災警報失敗');
    }
  };

  // 測試消防設備
  const handleTestFireDevice = async (deviceType: string, deviceId: string) => {
    try {
      await circularTrackService.testFireDevice(deviceType, deviceId);
      console.log('設備測試已啟動');
      
      // 重新獲取狀態
      const fireStatus = await circularTrackService.getFireSafetyStatus();
      setFireSafetyStatus(fireStatus);
    } catch (error) {
      console.error('設備測試失敗:', error);
      setError('設備測試失敗');
    }
  };

  // 查看消防設備詳情
  const handleViewFireDevice = async (deviceType: string, deviceId: string) => {
    try {
      const device = await circularTrackService.getFireDeviceDetails(deviceType, deviceId);
      setSelectedFireDevice({ ...device, type: deviceType });
      setFireDeviceModalOpen(true);
    } catch (error) {
      console.error('獲取設備詳情失敗:', error);
      setError('獲取設備詳情失敗');
    }
  };

  // 圖表數據處理
  const temperatureChartData = {
    labels: thermalData.slice(-12).map(d => new Date(d.timestamp).toLocaleTimeString()),
    datasets: [
      {
        label: '平均溫度 (°C)',
        data: thermalData.slice(-12).map(d => d.temperature),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.1
      },
      {
        label: '車輛數量',
        data: thermalData.slice(-12).map(d => d.vehicleCount || 0),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.1,
        yAxisID: 'y1'
      }
    ]
  };

  const statusChartData = anomalyStats ? {
    labels: ['警告', '嚴重', '緊急'],
    datasets: [
      {
        data: [
          anomalyStats.byLevel.warning,
          anomalyStats.byLevel.critical,
          anomalyStats.byLevel.emergency
        ],
        backgroundColor: ['#ffc107', '#fd7e14', '#dc3545'],
        borderWidth: 2
      }
    ]
  } : {
    labels: ['警告', '嚴重', '緊急'],
    datasets: [
      {
        data: [
          thermalAlerts.filter(a => a.alertLevel === 'warning').length,
          thermalAlerts.filter(a => a.alertLevel === 'critical').length,
          thermalAlerts.filter(a => a.alertLevel === 'emergency').length
        ],
        backgroundColor: ['#ffc107', '#fd7e14', '#dc3545'],
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: '溫度趨勢監控'
      }
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        grid: {
          drawOnChartArea: false,
        },
      }
    }
  };

  if (loading) {
    return (
      <Container fluid className="circular-track-container">
        <div className="text-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
          <p className="mt-2">正在載入環形軌道系統數據...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="circular-track-container">
        <Alert variant="danger" className="mt-3">
          <Alert.Heading>系統錯誤</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={loadInitialData}>
            重新載入
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="circular-track-container">
      {/* 系統性能監控 */}
      {error && (
        <Alert 
          variant="danger" 
          dismissible 
          onClose={() => setError(null)}
          className="mb-4"
        >
          <Alert.Heading>系統錯誤</Alert.Heading>
          <p>{error}</p>
          {retryCount > 0 && (
            <p className="mb-0">
              <small>已重試 {retryCount}/{MAX_RETRY_COUNT} 次</small>
            </p>
          )}
        </Alert>
      )}

      {loading && (
        <div className="text-center py-4 mb-4">
          <div className="spinner-border text-primary me-2" role="status"></div>
          <span>載入系統數據中...</span>
        </div>
      )}

      {!navigator.onLine && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>網絡連接異常</Alert.Heading>
          <p>檢測到網絡連接不穩定，部分功能可能受到影響。</p>
        </Alert>
      )}

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2>
              <i className="fas fa-route me-2"></i>
              環形軌道智能巡邏系統
            </h2>
            <div>
              <Button 
                variant={systemStatus?.autoPatrol ? "success" : "secondary"} 
                className="me-2"
                onClick={handleAutoPatrolToggle}
              >
                <i className="fas fa-play me-1"></i>
                {systemStatus?.autoPatrol ? '自動巡邏中' : '啟動巡邏'}
              </Button>
              <Button 
                variant="danger" 
                onClick={handleEmergencyStop}
                disabled={systemStatus?.emergencyMode}
              >
                <i className="fas fa-stop me-1"></i>
                緊急停止
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* 系統狀態警報 */}
      {systemStatus?.emergencyMode && (
        <Alert variant="danger" className="mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          系統處於緊急模式，所有軌道車已停止運行
        </Alert>
      )}

      {/* 性能監控提示 */}
      {dataCache && Object.keys(dataCache).length > 0 && (
        <Alert variant="info" className="mb-4">
          <i className="fas fa-info-circle me-2"></i>
          系統正在使用緩存數據以提升性能，數據更新間隔：30秒
        </Alert>
      )}

      {retryCount > 0 && (
        <Alert variant="warning" className="mb-4">
          <i className="fas fa-sync-alt me-2"></i>
          系統正在重試連接... ({retryCount}/{MAX_RETRY_COUNT})
        </Alert>
      )}

      {/* 系統控制面板 */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-cogs me-2"></i>
                系統控制面板
              </h5>
              <div>
                <Badge 
                  bg={systemStatus?.isActive ? 'success' : 'secondary'} 
                  className="me-2"
                >
                  {systemStatus?.isActive ? '系統運行中' : '系統停止'}
                </Badge>
                {systemStatus?.emergencyMode && (
                  <Badge bg="danger" className="me-2 blink">
                    緊急模式
                  </Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <div className="control-item">
                    <h6>自動巡邏</h6>
                    <Button
                      variant={systemStatus?.autoPatrol ? 'success' : 'outline-secondary'}
                      onClick={handleAutoPatrolToggle}
                      disabled={systemStatus?.emergencyMode}
                    >
                      <i className={`fas ${systemStatus?.autoPatrol ? 'fa-pause' : 'fa-play'} me-1`}></i>
                      {systemStatus?.autoPatrol ? '停止巡邏' : '開始巡邏'}
                    </Button>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="control-item">
                    <h6>緊急停止</h6>
                    <Button
                      variant="danger"
                      onClick={handleEmergencyStop}
                    >
                      <i className="fas fa-stop me-1"></i>
                      緊急停止
                    </Button>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="control-item">
                    <h6>消防系統</h6>
                    <Badge 
                      bg={fireSafetyStatus?.systemStatus.operational ? 'success' : 'warning'}
                      className="d-block p-2"
                    >
                      <i className={`fas ${fireSafetyStatus?.systemStatus.operational ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-1`}></i>
                      {fireSafetyStatus?.systemStatus.operational ? '就緒' : '檢查中'}
                    </Badge>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="control-item">
                    <h6>系統統計</h6>
                    <div className="stats-mini">
                      <div>軌道車: {trackVehicles.length}</div>
                      <div>活躍: {trackVehicles.filter(v => v.status === 'active').length}</div>
                      <div>警報: {thermalAlerts.filter(a => a.status === 'active').length}</div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
            {/* 消防安全狀態卡片 */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">消防安全狀態</h5>
            </Card.Header>
            <Card.Body>
            {fireSafetyStatus ? (
              <div>
                <div className="row g-2 g-md-4 mb-4">
                  <div className="col-6 col-md-3 text-center">
                    <div className={`h2 fw-bold ${fireSafetyStatus.systemStatus.systemMode === 'normal' ? 'text-success' : 'text-danger'}`}>
                      {fireSafetyStatus.systemStatus.systemMode === 'normal' ? '正常' : '警報'}
                    </div>
                    <div className="small text-muted">系統狀態</div>
                  </div>
                  <div className="col-6 col-md-3 text-center">
                    <div className="h2 fw-bold text-primary">
                      {fireSafetyStatus.deviceStatus.detectors.filter(d => d.status === 'normal').length}
                    </div>
                    <div className="small text-muted">活躍探測器</div>
                  </div>
                  <div className="col-6 col-md-3 text-center">
                    <div className="h2 fw-bold text-warning">
                      {fireSafetyStatus.deviceStatus.sprinklers.filter(s => s.status === 'standby').length}
                    </div>
                    <div className="small text-muted">就緒噴淋器</div>
                  </div>
                  <div className="col-6 col-md-3 text-center">
                    <div className="h2 fw-bold text-info">
                      {fireSafetyStatus.activeAlerts.length}
                    </div>
                    <div className="small text-muted">活躍警報</div>
                  </div>
                </div>

                {/* 消防設備狀態 */}
                <div className="row g-4 mb-4">
                  <div className="col-md-4">
                    <h4 className="fw-semibold mb-2">火災探測器</h4>
                    <div className="mb-2">
                      {fireSafetyStatus.deviceStatus.detectors.slice(0, 3).map((detector) => (
                        <div key={detector.id} className="d-flex justify-content-between align-items-center p-2 bg-light rounded mb-1">
                          <span className="small">{detector.zone}</span>
                          <Badge 
                            bg={detector.status === 'normal' ? 'success' : detector.status === 'alarm' ? 'danger' : 'warning'}
                            text={detector.status === 'normal' ? '正常' : detector.status === 'alarm' ? '警報' : '維護'}
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleViewFireDevice('detector', detector.id)}
                          >
                            詳情
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <h4 className="fw-semibold mb-2">噴淋系統</h4>
                    <div className="mb-2">
                      {fireSafetyStatus.deviceStatus.sprinklers.slice(0, 3).map((sprinkler) => (
                        <div key={sprinkler.id} className="d-flex justify-content-between align-items-center p-2 bg-light rounded mb-1">
                          <span className="small">噴淋器 {sprinkler.zone}區</span>
                          <Badge 
                            bg={sprinkler.status === 'standby' ? 'success' : sprinkler.status === 'active' ? 'primary' : 'warning'}
                            text={sprinkler.status === 'standby' ? '待機' : sprinkler.status === 'active' ? '啟動' : sprinkler.status === 'testing' ? '測試' : '故障'}
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleTestFireDevice('sprinkler', sprinkler.id)}
                          >
                            測試
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="col-md-4">
                    <h4 className="fw-semibold mb-2">泡沫系統</h4>
                    <div className="mb-2">
                      {fireSafetyStatus.deviceStatus.foamSystems.slice(0, 3).map((foam) => (
                        <div key={foam.id} className="d-flex justify-content-between align-items-center p-2 bg-light rounded mb-1">
                          <span className="small">泡沫系統 {foam.id}</span>
                          <Badge 
                            bg={foam.status === 'standby' ? 'success' : foam.status === 'active' ? 'primary' : 'warning'}
                            text={foam.status === 'standby' ? '就緒' : foam.status === 'active' ? '啟動' : '維護'}
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleTestFireDevice('foam', foam.id)}
                          >
                            測試
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 活躍警報 */}
                {fireSafetyStatus.activeAlerts.length > 0 && (
                  <div className="mb-4">
                    <h4 className="fw-semibold mb-2 text-danger">活躍火災警報</h4>
                    <div className="mb-2">
                      {fireSafetyStatus.activeAlerts.map((alert) => (
                        <div key={alert.id} className="p-3 bg-danger bg-opacity-10 border border-danger rounded mb-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-medium text-danger">
                                區域 {alert.zone} - {alert.type === 'fire' ? '火災' : alert.type === 'manual_fire' ? '手動火災' : '警告'}
                              </div>
                              <div className="small text-danger">
                                位置: {alert.position}% | 嚴重程度: {alert.severity}
                              </div>
                              <div className="small text-muted">
                                {new Date(alert.createdAt).toLocaleString()}
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              <Button 
                                size="sm" 
                                variant="danger"
                                onClick={() => handleFireSystemControl('resolve_alert', { alertId: alert.id })}
                              >
                                解決
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 消防控制按鈕 */}
                <div className="d-flex gap-2">
                  <Button 
                    variant="danger"
                    onClick={() => handleManualFireAlarm(1, '手動觸發測試')}
                  >
                    手動火災警報
                  </Button>
                  <Button 
                    variant="secondary"
                    onClick={() => handleFireSystemControl('reset_system')}
                  >
                    重置系統
                  </Button>
                  <Button 
                    variant="outline-primary"
                    onClick={() => handleFireSystemControl('test_all_devices')}
                  >
                    測試所有設備
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
                <div className="mt-2">載入消防系統狀態...</div>
              </div>
            )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 軌道車狀態卡片 */}
      <Row className="mb-4">
        {trackVehicles.map(vehicle => (
          <Col md={6} key={vehicle.id} className="mb-3">
            <Card className="h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{vehicle.name} ({vehicle.id})</h5>
                {getStatusBadge(vehicle.status)}
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={6}>
                    <div className="mb-2">
                      <small className="text-muted">軌道位置</small>
                      <ProgressBar now={vehicle.position} label={`${vehicle.position.toFixed(1)}%`} />
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">電池電量</small>
                      <ProgressBar 
                        now={vehicle.batteryLevel} 
                        label={`${vehicle.batteryLevel.toFixed(1)}%`}
                        variant={vehicle.batteryLevel > 20 ? 'success' : 'danger'}
                      />
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="mb-2">
                      <i className="fas fa-thermometer-half me-2"></i>
                      <span>車體溫度: {vehicle.temperature.toFixed(1)}°C</span>
                    </div>
                    <div className="mb-2">
                      <i className="fas fa-camera me-2"></i>
                      <span>熱成像: </span>
                      <Badge bg={vehicle.thermalCamera.status === 'online' ? 'success' : 'danger'}>
                        {vehicle.thermalCamera.status === 'online' ? '在線' : '離線'}
                      </Badge>
                    </div>
                  </Col>
                </Row>
                <div className="mt-3">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={() => {
                      setSelectedVehicle(vehicle);
                      setShowControlModal(true);
                    }}
                  >
                    <i className="fas fa-cog me-1"></i>
                    控制
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 熱成像監控和警報 */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-fire me-2"></i>
                熱成像異常警報
              </h5>
            </Card.Header>
            <Card.Body>
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>時間</th>
                    <th>車輛</th>
                    <th>位置</th>
                    <th>溫度</th>
                    <th>等級</th>
                    <th>狀態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {thermalAlerts.map(alert => (
                    <tr key={alert.id}>
                      <td>{new Date(alert.timestamp).toLocaleString()}</td>
                      <td>{alert.vehicleId}</td>
                      <td>{alert.location}</td>
                      <td className="text-danger fw-bold">{alert.temperature}°C</td>
                      <td>{getAlertBadge(alert.alertLevel)}</td>
                      <td>
                        <Badge bg={alert.status === 'active' ? 'danger' : alert.status === 'resolved' ? 'success' : 'warning'}>
                          {alert.status === 'active' ? '進行中' : alert.status === 'resolved' ? '已解決' : '調查中'}
                        </Badge>
                      </td>
                      <td>
                        {alert.status === 'active' && alert.alertLevel === 'critical' && (
                          <Button 
                            variant="danger" 
                            size="sm"
                            onClick={() => handleFireAlert(alert.id)}
                          >
                            <i className="fas fa-fire-extinguisher me-1"></i>
                            消防
                          </Button>
                        )}
                        <Button 
                          variant="outline-info" 
                          size="sm"
                          className="ms-1"
                          onClick={() => {
                            setSelectedAlert(alert);
                            setShowAlertModal(true);
                          }}
                        >
                          詳情
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">警報統計</h6>
            </Card.Header>
            <Card.Body>
              <Doughnut data={statusChartData} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 溫度趨勢圖 */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-line me-2"></i>
                溫度趨勢監控
              </h5>
            </Card.Header>
            <Card.Body>
              <Line data={temperatureChartData} options={chartOptions} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 軌道車控制模態框 */}
      <Modal show={showControlModal} onHide={() => setShowControlModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>軌道車控制 - {selectedVehicle?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVehicle && (
            <div>
              <div className="mb-3">
                <p><strong>車輛ID:</strong> {selectedVehicle.id}</p>
                <p><strong>當前狀態:</strong> {getStatusBadge(selectedVehicle.status)}</p>
                <p><strong>電池電量:</strong> {selectedVehicle.batteryLevel}%</p>
                <p><strong>當前溫度:</strong> {selectedVehicle.temperature}°C</p>
                <p><strong>軌道位置:</strong> {selectedVehicle.position.toFixed(1)}%</p>
              </div>
              
              <hr />
              
              <h6>控制操作:</h6>
              <div className="d-grid gap-2">
                <Button 
                  variant="success" 
                  onClick={() => handleVehicleControl(selectedVehicle.id, 'start')}
                  disabled={selectedVehicle.status === 'active'}
                >
                  <i className="fas fa-play me-1"></i>
                  啟動巡邏
                </Button>
                <Button 
                  variant="warning" 
                  onClick={() => handleVehicleControl(selectedVehicle.id, 'stop')}
                  disabled={selectedVehicle.status === 'idle'}
                >
                  <i className="fas fa-pause me-1"></i>
                  停止巡邏
                </Button>
                <Button 
                  variant="info" 
                  onClick={() => handleVehicleControl(selectedVehicle.id, 'charge')}
                  disabled={selectedVehicle.status === 'charging'}
                >
                  <i className="fas fa-battery-three-quarters me-1"></i>
                  返回充電
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => handleVehicleControl(selectedVehicle.id, 'maintenance')}
                  disabled={selectedVehicle.status === 'maintenance'}
                >
                  <i className="fas fa-tools me-1"></i>
                  維護模式
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => handleVehicleControl(selectedVehicle.id, 'emergency')}
                >
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  緊急停止
                </Button>
              </div>
              
              <hr />
              
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>巡邏模式</Form.Label>
                      <Form.Select>
                        <option>自動巡邏</option>
                        <option>手動控制</option>
                        <option>定點監控</option>
                        <option>返回充電</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>巡邏速度</Form.Label>
                      <Form.Range min={1} max={10} defaultValue={5} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>熱成像靈敏度</Form.Label>
                      <Form.Select>
                        <option>低</option>
                        <option>中</option>
                        <option>高</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>警報閾值 (°C)</Form.Label>
                      <Form.Control type="number" defaultValue={60} min={40} max={100} />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowControlModal(false)}>
            取消
          </Button>
          <Button variant="primary">
            應用設定
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 警報詳情模態框 */}
      <Modal show={showAlertModal} onHide={() => setShowAlertModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>警報詳情 - {selectedAlert?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAlert && (
            <div>
              <p><strong>車輛ID:</strong> {selectedAlert.vehicleId}</p>
              <p><strong>位置:</strong> {selectedAlert.location}</p>
              <p><strong>溫度:</strong> {selectedAlert.temperature}°C</p>
              <p><strong>警報等級:</strong> {getAlertBadge(selectedAlert.alertLevel)}</p>
              <p><strong>檢測時間:</strong> {new Date(selectedAlert.timestamp).toLocaleString()}</p>
              <p><strong>描述:</strong> {selectedAlert.description}</p>
              
              <hr />
              
              <h6>狀態更新:</h6>
              <div className="d-grid gap-2">
                <Button 
                  variant="info" 
                  onClick={() => handleAlertStatusUpdate(selectedAlert.id, 'investigating')}
                  disabled={selectedAlert.status === 'investigating'}
                >
                  <i className="fas fa-search me-1"></i>
                  調查中
                </Button>
                <Button 
                  variant="success" 
                  onClick={() => handleAlertStatusUpdate(selectedAlert.id, 'resolved')}
                  disabled={selectedAlert.status === 'resolved'}
                >
                  <i className="fas fa-check me-1"></i>
                  已解決
                </Button>
                {selectedAlert.alertLevel === 'critical' && (
                  <Button 
                    variant="danger" 
                    onClick={() => handleFireAlert(selectedAlert.id)}
                  >
                    <i className="fas fa-fire-extinguisher me-1"></i>
                    觸發消防警報
                  </Button>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAlertModal(false)}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 消防設備詳情模態框 */}
      <Modal
        show={fireDeviceModalOpen}
        onHide={() => setFireDeviceModalOpen(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>消防設備詳情</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFireDevice && (
            <div className="space-y-4">
              <div>
                <strong>設備ID:</strong> {selectedFireDevice.id}
              </div>
              <div>
                <strong>設備類型:</strong> {
                  selectedFireDevice.type === 'detector' ? '火災探測器' :
                  selectedFireDevice.type === 'sprinkler' ? '噴淋器' :
                  selectedFireDevice.type === 'foam' ? '泡沫系統' : '氣體滅火'
                }
              </div>
              <div>
                <strong>位置:</strong> {selectedFireDevice.location}
              </div>
              <div>
                <strong>狀態:</strong> 
                <Badge 
                  bg={
                    selectedFireDevice.status === 'active' || selectedFireDevice.status === 'ready' ? 'success' :
                    selectedFireDevice.status === 'alarm' ? 'danger' : 'warning'
                  }
                  className="ms-2"
                >
                  {
                    selectedFireDevice.status === 'active' ? '正常' :
                    selectedFireDevice.status === 'ready' ? '就緒' :
                    selectedFireDevice.status === 'alarm' ? '警報' : '維護'
                  }
                </Badge>
              </div>
              {selectedFireDevice.temperature && (
                <div>
                  <strong>溫度:</strong> {selectedFireDevice.temperature}°C
                </div>
              )}
              {selectedFireDevice.smokeLevel && (
                <div>
                  <strong>煙霧濃度:</strong> {selectedFireDevice.smokeLevel}%
                </div>
              )}
              {selectedFireDevice.pressure && (
                <div>
                  <strong>壓力:</strong> {selectedFireDevice.pressure} bar
                </div>
              )}
              {selectedFireDevice.flowRate && (
                <div>
                  <strong>流量:</strong> {selectedFireDevice.flowRate} L/min
                </div>
              )}
              {selectedFireDevice.lastMaintenance && (
                <div>
                  <strong>上次維護:</strong> {new Date(selectedFireDevice.lastMaintenance).toLocaleDateString()}
                </div>
              )}
              {selectedFireDevice.nextMaintenance && (
                <div>
                  <strong>下次維護:</strong> {new Date(selectedFireDevice.nextMaintenance).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setFireDeviceModalOpen(false)}>
            關閉
          </Button>
          {selectedFireDevice && (
            <Button 
              variant="primary"
              onClick={() => {
                handleTestFireDevice(selectedFireDevice.type, selectedFireDevice.id);
                setFireDeviceModalOpen(false);
              }}
            >
              測試設備
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CircularTrack;